import { HelpArticle, HelpLang } from './help.models';

export function normalizeText(value: string): string {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9ñ\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** "registro" ~ "registrar", "movimenti" ~ "movimento" */
const stem = (term: string) => (term.length >= 6 ? term.slice(0, -2) : term);

const STOP_WORDS: Record<HelpLang, Set<string>> = {
  es: new Set([
    'como', 'el', 'la', 'los', 'las', 'un', 'una', 'unos', 'unas', 'de', 'del', 'en', 'y', 'o', 'a', 'mi', 'mis',
    'se', 'que', 'para', 'por', 'con', 'puedo', 'hago', 'hacer', 'donde', 'quiero', 'al', 'lo', 'le', 'es',
    'me', 'su', 'sus', 'cual', 'necesito',
  ]),
  it: new Set([
    'come', 'il', 'lo', 'la', 'i', 'gli', 'le', 'un', 'uno', 'una', 'di', 'del', 'della', 'dei', 'delle', 'in',
    'e', 'o', 'a', 'al', 'alla', 'mio', 'mia', 'miei', 'mie', 'si', 'che', 'per', 'con', 'posso', 'faccio',
    'fare', 'dove', 'voglio', 'devo', 'su', 'da', 'ho', 'nel', 'nella', 'quale',
  ]),
};

export function searchTerms(query: string, lang: HelpLang): string[] {
  const stop = STOP_WORDS[lang];
  return normalizeText(query)
    .split(' ')
    .filter(term => term.length > 1 && !stop.has(term));
}

export function searchArticles(
  articles: HelpArticle[],
  query: string,
  lang: HelpLang,
  limit = 8
): HelpArticle[] {
  const terms = searchTerms(query, lang);
  if (!terms.length) return [];
  const needed = Math.ceil(terms.length / 2);

  return articles
    .map(article => {
      const title = normalizeText(article.title[lang]);
      const summary = normalizeText(article.summary[lang]);
      const keywords = normalizeText(article.keywords[lang].join(' '));
      const body = normalizeText(article.steps[lang].join(' ') + ' ' + (article.tip?.[lang] ?? ''));
      let score = 0;
      let matched = 0;
      for (const term of terms) {
        const root = stem(term);
        let hit = false;
        if (title.includes(root)) { score += 6; hit = true; }
        if (keywords.includes(root)) { score += 4; hit = true; }
        if (summary.includes(root)) { score += 2; hit = true; }
        if (body.includes(root)) { score += 1; hit = true; }
        if (hit) matched++;
      }
      return { article, matched, score };
    })
    .filter(entry => entry.matched >= needed)
    .sort((a, b) => b.matched - a.matched || b.score - a.score)
    .slice(0, limit)
    .map(entry => entry.article);
}
